import React, { useMemo } from "react";
// ANT DESIGN COMPONENTS
import { Tooltip, Tag } from "antd";
// REDUX
import { useSelector, useDispatch } from "react-redux";
import {
  updateLiftSelected,
  updateBankSelected,
  updateModalControlPanelTab,
  updateOpenModalControlPanelLift,
  updateControlPanelDrawerPlacement,
  updateChannelId,
  updateServerId,
} from "../../../redux/monitoring/monitoringSlice";
// CUSTOM HOOKS
import {
  useLastLiftMessage,
  useDirectionForLift,
} from "../socketMessageState/BankMessagesContext";
// COMPONENTS
import IconDefinition from "../iconDefinition/IconDefinition";
import IconDefinitionEscalatorDefault from "../iconDefinition/IconDefinitionEscalatorDefault";
// INTERFACES
import { SocketMessageInterface } from "../../../interfaces/Monitoring.interface";
import { RootState } from "../../../app/store";
import { BankInterface } from "../../../interfaces/Bank.interface";
import { LiftInterface } from "../../../interfaces/Lift.interface";
// AUTHORIZATION
import validatePermission from "../../../utils/validatePermissions";
// UTILS
import { emptyFunctionDeclaration } from "../../../utils/utils";
import { getServiceModeDisplay } from "../../../utils/serviceModeDisplay";
import {
  bitmaskToDoorIcon,
  bitmaskToDoorIconFiltered,
} from "../../../utils/bitmaskToIcon";
import { bitmaskPrefix } from "../../../utils/typedKeys";
// CONSTANTS
import { BLANK_SOCKET_MESSAGE } from "../../../constants/monitoring";
import { stringToUuidT } from "../../../utils/uuid";

function MonitoringLiftStatusView({
  lift,
  bank,
  serverId,
  channelId,
}: {
  lift: LiftInterface;
  bank: BankInterface;
  serverId: string;
  channelId: string;
}) {
  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */
  const dispatch = useDispatch();

  // ************************************************ */
  // REDUX SLICE VARIABLES ************************** */
  const { theme } = useSelector((state: RootState) => state.home);
  const { permissions } = useSelector((state: RootState) => state.user);
  const doorUnknownAsOpening = useSelector(
    (state: RootState) => state.monitoring.doorUnknownAsOpening
  );
  const hideUnconfiguredDoors = useSelector(
    (state: RootState) => state.monitoring.hideUnconfiguredDoors
  );
  const previewFaultServiceMode = useSelector(
    (state: RootState) => state.monitoring.previewFaultServiceMode
  );

  // ************************************************ */
  // CUSTOM HOOKS *********************************** */
  const liftUuid = useMemo(() => stringToUuidT(lift.id), [lift.id]);
  const socketMessageLift: SocketMessageInterface =
    useLastLiftMessage(liftUuid) || BLANK_SOCKET_MESSAGE;
  const hasFaults: boolean = previewFaultServiceMode || socketMessageLift.faults;

  const prefix = useMemo(
    () => bitmaskPrefix(socketMessageLift.bankId, socketMessageLift.liftId),
    [socketMessageLift.bankId, socketMessageLift.liftId]
  );
  const hasFrontDoor = useMemo(
    () => lift.lift_floors?.some((lf) => lf.front_door) ?? true,
    [lift.lift_floors]
  );
  const hasRearDoor = useMemo(
    () => lift.lift_floors?.some((lf) => lf.rear_door) ?? true,
    [lift.lift_floors]
  );
  const doorIcon = useMemo(
    () =>
      hideUnconfiguredDoors
        ? bitmaskToDoorIconFiltered(
          socketMessageLift.bitmask,
          prefix,
          hasFrontDoor,
          hasRearDoor,
          doorUnknownAsOpening
        )
        : bitmaskToDoorIcon(
          socketMessageLift.bitmask,
          prefix,
          doorUnknownAsOpening
        ),
    [
      socketMessageLift.bitmask,
      prefix,
      doorUnknownAsOpening,
      hideUnconfiguredDoors,
      hasFrontDoor,
      hasRearDoor,
    ]
  );
  const { directionIcon, lastDirectionIcon } = useDirectionForLift(liftUuid);

  // ************************************************ */
  // SERVICES AND API CALLS ************************* */

  // ************************************************ */
  // FUNCTIONS ************************************** */
  const getTabKey = () => {
    if (hasFaults) {
      return "FAULTS";
    }
    if (validatePermission("monitor-control-panel-car-calls", permissions)) {
      return "CARCALLS";
    }
    if (validatePermission("monitor-control-panel-hall-calls", permissions)) {
      return "HALLCALLS";
    }
    if (validatePermission("monitor-control-panel-actions", permissions)) {
      return "ACTIONS";
    }
    if (validatePermission("monitor-control-panel-faults", permissions)) {
      return "FAULTS";
    }
    if (validatePermission("monitor-control-panel-status", permissions)) {
      return "STATUS";
    }
    return "CARCALLS";
  };

  const getDrawerPlacement = (e: any) => {
    const clickX = e.clientX;
    const screenWidth = window.innerWidth;
    if (clickX > screenWidth / 2) {
      dispatch(updateControlPanelDrawerPlacement("left"));
    } else {
      dispatch(updateControlPanelDrawerPlacement("right"));
    }
  };

  const isEscalator = bank.type === "ESCALATOR" || bank.type === "MOVINGWALK";

  const onClickOpenModalElevator = (e: any) => {
    getDrawerPlacement(e);
    dispatch(
      updateModalControlPanelTab({
        liftType: isEscalator ? "ESCALATOR" : "ELEVATOR",
        tab: getTabKey(),
      })
    );
    dispatch(updateLiftSelected(lift));
    dispatch(updateBankSelected(bank));
    dispatch(updateChannelId(channelId));
    dispatch(updateServerId(serverId));
    dispatch(updateOpenModalControlPanelLift(true));
  };

  // ************************************************ */
  // COMPONENT ************************************** */
  const displayDirection = directionIcon || lastDirectionIcon;
  const directionDimmed = !directionIcon && !!lastDirectionIcon;

  const renderIcon = () => {
    if (isEscalator) {
      return (
        <IconDefinitionEscalatorDefault
          iconSize={28}
          mapViewClass=""
          lift={lift}
        />
      );
    }
    if (!socketMessageLift.connected) {
      return (
        <IconDefinition
          isMap={false}
          expanded={false}
          liftType="ELEVATOR"
          icon="elOutIndicator"
          iconSize={16}
        />
      );
    }
    return (
      <>
        {doorIcon && (
          <IconDefinition
            isMap={false}
            expanded={false}
            liftType="ELEVATOR"
            icon={doorIcon}
            iconSize={16}
          />
        )}
        {displayDirection && (
          <span style={{ opacity: directionDimmed ? 0.3 : 1 }}>
            <IconDefinition
              isMap={false}
              expanded={false}
              liftType="ELEVATOR"
              icon={displayDirection}
              iconSize={16}
            />
          </span>
        )}
      </>
    );
  };

  return (
    <div className="mt-1">
      <div
        className={`monitoringView__topViewContainer__${theme}  ${hasFaults ? `monitoringView__dangerElevator   ` : ""
          }${hasFaults ? `monitoringView__dangerElevator__top ` : ""}${hasFaults ? `monitoringView__dangerElevator__bottom` : ""
          }  `}
        style={{
          height: "30px",
          width: "45px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          marginLeft: "6px",
          marginRight: "6px",
          marginTop: "-10px",
        }}
      >
        {renderIcon()}
      </div>
      {/** ELEVATOR NAME */}
      <div
        role="button"
        tabIndex={0}
        onKeyDown={emptyFunctionDeclaration}
        onClick={onClickOpenModalElevator}
        style={{
          marginTop: "5px",
          fontSize: "9px",
          fontWeight: "bolder",
          textAlign: "center",
        }}
      >
        <div>
          <Tooltip placement="top" title={lift.name}>
            <span className={hasFaults ? "generalStyles__error" : ""}>
              {lift.name.toUpperCase().substring(0, 6)}
            </span>
          </Tooltip>
        </div>
        {hasFaults && (
          <div>
            <Tag
              style={{
                marginTop: "3px",
                fontSize: "10px",
                width: "90%",
                marginLeft: "5%",
                marginRight: "5%",
              }}
              color="red"
            >
              FAULT
            </Tag>
          </div>
        )}
        {(socketMessageLift.serviceMode || previewFaultServiceMode) &&
          (() => {
            if (previewFaultServiceMode && !socketMessageLift.serviceMode) {
              return (
                <div>
                  <Tooltip placement="top" title="Energy Saving">
                    <Tag
                      style={{
                        marginTop: "3px",
                        fontSize: "10px",
                        width: "90%",
                        marginLeft: "5%",
                        marginRight: "5%",
                      }}
                      color="blue"
                    >
                      ENER
                    </Tag>
                  </Tooltip>
                </div>
              );
            }
            const display = getServiceModeDisplay(
              socketMessageLift.serviceMode.messageType,
              socketMessageLift.serviceMode.message?.message ?? null
            );
            if (!display) return null;
            return (
              <div>
                <Tooltip placement="top" title={display.displayName}>
                  <Tag
                    style={{
                      marginTop: "3px",
                      fontSize: "10px",
                      width: "90%",
                      marginLeft: "5%",
                      marginRight: "5%",
                    }}
                    color={display.color}
                  >
                    {display.abbreviation.substring(0, 4)}
                  </Tag>
                </Tooltip>
              </div>
            );
          })()}
      </div>
    </div>
  );
}

export default React.memo(MonitoringLiftStatusView);
